import { useEffect, useState } from "react";
import type {
  CreateSubscriptionLinkRequest,
  SubscriptionLinkPreview,
  SubscriptionPlan,
} from "@care-by-ella/contracts";
import { fetchSubscriptionPlans } from "@/lib/clinician-api";
import { underhallBlockedText } from "@/lib/earlier-treatment-start";
import { useUnderhallEligibility } from "./PatientEarlierTreatmentStart";

// TES-907 (Ö-221) — the doctor picks the plan before the link goes out.
//
// Emelie 2026-09-23:
// "Den generella länken ersätts av ett uttryckligt abonnemangsval med förhandsvisning och auditlogg, inget förvalt."
//
// So: two options, NOTHING pre-selected, and nothing can be sent until he has
// chosen. The server refuses an unchosen request anyway — this is the same
// rule said twice on purpose, because the one that reaches Kirill is this one.
//
// THE PREVIEW is what he is told before he sends, not after: the price she
// will be charged and whether she keeps her coach. The coach line is not
// decoration — the server derives it from `coachAccessFor`, the single reading
// of "Ella Underhåll: coach_access=INACTIVE", and choosing that plan ends her
// coach contact.
//
// It comes from the SERVER, out of tenant config — the same source checkout
// charges from. A price restated here would be a second place for it to live,
// and the one that drifts is always the copy nobody bills against.
//
// TES-952 — the choice now lives INSIDE the positive treatment decision, so
// the fields are a controlled component the decision workspace owns, and the
// send button survives only as the re-send once a decision is published.
const kr = (minor: number | null) =>
  minor === null
    ? "pris saknas i konfigurationen"
    : `${Math.round(minor / 100)} kr/mån`;

export interface PlanChoiceValue {
  // `null`, not a default. This is what "inget förvalt" looks like in state.
  plan: SubscriptionPlan | null;
  basis: string;
}

export const EMPTY_PLAN_CHOICE: PlanChoiceValue = { plan: null, basis: "" };

// The request body, or null while the choice is incomplete: no plan, or the
// maintenance plan without the twelve-month basis the server requires.
export function planChoicePayload(
  value: PlanChoiceValue,
): CreateSubscriptionLinkRequest | null {
  if (value.plan === null) return null;
  if (value.plan === "ella_underhall") {
    const basis = value.basis.trim();
    return basis ? { plan: value.plan, maintenanceBasis: basis } : null;
  }
  return { plan: value.plan };
}

export function SubscriptionPlanFields({
  patientId,
  value,
  onChange,
  disabled = false,
  name,
}: {
  patientId: string;
  value: PlanChoiceValue;
  onChange: (next: PlanChoiceValue) => void;
  disabled?: boolean;
  // The radio group's name; two groups on one page must not share one.
  name: string;
}) {
  const [plans, setPlans] = useState<SubscriptionLinkPreview[] | null>(null);
  // TES-1073 (Ö-297) — without twelve documented months Ella Uppstart is the
  // only selectable plan. The server refuses "Underhåll" regardless; this is
  // the same rule said where the doctor chooses, with the reason.
  const underhall = useUnderhallEligibility(patientId);
  const blocked =
    underhall === null
      ? "Kontrollerar behandlingsstart…"
      : underhallBlockedText(underhall);

  // A prefilled "Underhåll" (her F-07 choice, a correction) that the rule now
  // refuses is cleared rather than left checked on a disabled option.
  useEffect(() => {
    if (blocked && underhall !== null && value.plan === "ella_underhall") {
      onChange({ ...value, plan: null });
    }
  }, [blocked, underhall, value, onChange]);

  useEffect(() => {
    let live = true;
    void fetchSubscriptionPlans()
      .then((r) => {
        if (live) setPlans(r.plans);
      })
      // A failed preview must not hide the control: the doctor can still
      // choose, he simply is not shown the price. Silently, because this sits
      // beside the page's own error banner and two messages about one hiccup
      // is worse than one.
      .catch(() => {
        if (live) setPlans([]);
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="flex flex-col gap-xs">
      <p className="text-xs text-textSecondary">
        Välj abonnemangslänk. Inget är förvalt.
      </p>
      {(plans ?? []).map((p) => {
        const reason = p.plan === "ella_underhall" ? blocked : null;
        return (
          <label
            key={p.plan}
            className={`flex items-start gap-xs text-xs ${reason ? "opacity-60" : ""}`}
          >
            <input
              type="radio"
              name={name}
              checked={value.plan === p.plan}
              disabled={disabled || reason !== null}
              onChange={() => onChange({ ...value, plan: p.plan })}
              className="mt-[2px]"
            />
            <span>
              <strong>{p.label}</strong> — {kr(p.amountMinor)}
              <br />
              <span className="text-textSecondary">
                {p.coachAccess === "active"
                  ? "Patienten behåller sin coach."
                  : "Coachkontakten avslutas. Tidigare coachchatt går fortfarande att läsa."}
              </span>
              {reason ? (
                <span
                  className="block text-warning"
                  data-testid="underhall-blocked"
                >
                  {reason}
                </span>
              ) : null}
            </span>
          </label>
        );
      })}
      {plans === null ? (
        <span className="text-xs text-textSecondary">Hämtar abonnemang…</span>
      ) : null}

      {value.plan === "ella_underhall" ? (
        <label className="block text-xs text-textSecondary">
          {/* Ö-221: "minst tolv månaders verifierad medicinsk viktbehandling totalt; tid hos tidigare vårdgivare får räknas."
              We hold no data
              about care elsewhere, so this is the doctor asserting it. */}
          Underlag för tolv månaders behandling (obligatoriskt, syns inte för
          patienten)
          <textarea
            value={value.basis}
            onChange={(e) => onChange({ ...value, basis: e.target.value })}
            rows={2}
            disabled={disabled}
            placeholder="T.ex. 14 mån hos tidigare vårdgivare, journalutdrag granskat"
            className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs"
          />
        </label>
      ) : null}
    </div>
  );
}

// The re-send, once a positive decision is published. A link lives 72 hours,
// so the doctor still needs a way to send one — just not as a header action
// that could go out before any decision (the server's TES-902 gate refuses
// that anyway).
//
// TES-1055 (Emelie, point J) — for a transfer patient `initial` is the plan
// her F-07 choice (or the doctor's logged correction) names, preselected:
// "normalt skicka den förifyllda abonnemangslänk som motsvarar patientens val".
// Without it nothing is preselected.
export function SubscriptionPlanChoice({
  patientId,
  busy,
  onSend,
  initial,
}: {
  patientId: string;
  busy: boolean;
  onSend: (choice: CreateSubscriptionLinkRequest) => void;
  initial?: PlanChoiceValue | null;
}) {
  const [value, setValue] = useState<PlanChoiceValue>(
    initial ?? EMPTY_PLAN_CHOICE,
  );
  const payload = planChoicePayload(value);
  return (
    <div className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm">
      <SubscriptionPlanFields
        patientId={patientId}
        value={value}
        onChange={setValue}
        disabled={busy}
        name="subscriptionPlanResend"
      />
      <button
        type="button"
        disabled={busy || payload === null}
        onClick={() => payload && onSend(payload)}
        className="w-fit rounded-md border border-surfaceMuted px-sm py-xs text-xs disabled:opacity-50"
      >
        {busy ? "Skickar…" : "Skicka ny abonnemangslänk"}
      </button>
    </div>
  );
}
